import "./Input.scss"

import React, { ChangeEvent, KeyboardEvent, ClipboardEvent, ReactNode, useRef, useEffect, useState, forwardRef } from "react"
import i18n from "intl/i18n"
import cn from "classnames"

import { INPUT_MAX_LENGTH_DEFAULT, GroupedControl } from "config/constants"

type InputType = "text" | "email" | "password" | "url" | "textarea"

export interface IInputProps {
    id?: string
    className?: string
    inputClassName?: string
    theme?: "default" | "white"
    width?: string
    name?: string
    value?: string | number
    readOnly?: boolean
    required?: boolean
    placeholder?: string
    type?: InputType
    disabled?: boolean
    autoFocus?: boolean
    minLength?: number
    maxLength?: number
    isInvalid?: boolean
    rows?: number
    onChange?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
    onPaste?: (event: ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void
    onBlur?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
    onFocus?: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
    onKeyPress?: (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void
    before?: ReactNode
    after?: ReactNode
    grouped?: GroupedControl
    style?: React.CSSProperties
    testId?: string
}

const Input = forwardRef<any, React.PropsWithChildren<IInputProps>>(({
    id,
    className = "",
    inputClassName = "",
    theme = "default",
    width,
    name,
    value,
    placeholder = i18n.t('common.pleaseEnter'),
    type = "text",
    readOnly = false,
    required = false,
    disabled = false,
    autoFocus = false,
    minLength,
    maxLength = INPUT_MAX_LENGTH_DEFAULT,
    isInvalid = false,
    rows = 5,
    onChange,
    onPaste,
    onBlur,
    onFocus,
    onKeyPress,
    before,
    after,
    grouped,
    testId = "input",
    style = {},
    ...others
}, ref) => {
    const beforeRef = useRef<HTMLDivElement>(null)
    const afterRef = useRef<HTMLDivElement>(null)
    const [inputPaddingLeft, setInputPaddingLeft] = useState("")
    const [inputPaddingRight, setInputPaddingRight] = useState("")

    const commonProps: React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
        & React.DetailedHTMLProps<React.TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement> = {
        className: cn(
            "input__input",
            { "input__input--invalid": isInvalid },
            inputClassName,
        ),
        style: {
            width,
            paddingLeft: inputPaddingLeft,
            paddingRight: inputPaddingRight,
            ...style,
        },
        disabled,
        autoFocus,
        readOnly,
        required,
        value,
        name,
        id,
        minLength,
        maxLength,
        placeholder,
        onChange,
        onPaste,
        onBlur,
        onFocus,
        onKeyPress,
        autoComplete: "off",
        ref,
    }

    const inputProps = {
        ...commonProps,
        type,
    }

    const textareaProps = {
        ...commonProps,
        rows,
    }

    const inputContent = () => {
        if (type === "textarea") {
            return <textarea data-testid={testId} {...{ ...textareaProps, ...others }}></textarea>
        }
        return <input data-testid={testId} {...{ ...inputProps, ...others }} />
    }

    useEffect(() => {
        if (beforeRef.current) {
            setInputPaddingLeft(`${beforeRef.current.clientWidth}px`)
        } else {
            setInputPaddingLeft("")
        }

        if (afterRef.current) {
            setInputPaddingRight(`${afterRef.current.clientWidth}px`)
        } else {
            setInputPaddingRight("")
        }
    }, [])

    const input = (
        <div className={cn([
            "input",
            `input--theme-${theme}`,
            className,
            grouped ? `input--grouped-${grouped}` : "",
        ])}
            style={{ width }}>
            {!!before &&
                <div className="input__before" ref={beforeRef}>
                    {before}
                </div>
            }
            {inputContent()}
            {!!after &&
                <div className="input__after" ref={afterRef}>
                    {after}
                </div>
            }
        </div>
    )

    return input
})

Input.displayName = "Input"

export default Input
